import { useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Popover,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Chip,
  Paper,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import useResponsive from '../../hooks/useResponsive';

// 30분 단위로 시간 옵션 생성 (00:00 ~ 23:30)
const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      options.push(timeStr);
    }
  }
  // 24:00 추가 (끝시간용)
  options.push('24:00');
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

const WeekdayTimeRangeSelector = ({
  weekdayName, 
  ranges, 
  onChange,
  allRanges, 
  setAllRanges, 
  weekdayIndex, 
  weekdayLabelList
}) => {
  const smUp = useResponsive('up', 'sm');
  const [copyAnchorEl, setCopyAnchorEl] = useState(null);
  const [copyChecked, setCopyChecked] = useState([]);

  // 인덱스를 시간 문자열로 변환
  const indexToTime = (idx) => {
    const hour = Math.floor(idx / 2);
    const minute = idx % 2 === 0 ? 0 : 30;
    if (hour >= 24) return '24:00';
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  };

  // 시간 문자열을 인덱스로 변환
  const timeToIndex = (timeStr) => {
    if (timeStr === '24:00') return 48;
    const [hour, minute] = timeStr.split(':').map(Number);
    return hour * 2 + (minute === 30 ? 1 : 0);
  };

  // 두 범위가 겹치는지 확인 (정확한 겹침 검사)
  const isOverlapping = (range1, range2) => {
    // range1.end와 range2.start가 같거나, range1.start와 range2.end가 같은 경우는 겹치지 않음 (연속)
    return !(range1.end < range2.start || range1.start > range2.end);
  };

  // 특정 시간 범위가 기존 범위와 겹치는지 확인
  const wouldOverlap = (newStart, newEnd, excludeIndex = -1) => {
    return ranges.some((range, index) => {
      if (index === excludeIndex) return false;
      return isOverlapping({ start: newStart, end: newEnd }, range);
    });
  };

  // 시작시간 옵션: 모든 시간 표시 + 선택 불가 항목 disabled 마킹
  const getStartTimeOptions = (currentRangeIndex) => {
    const currentRange = ranges[currentRangeIndex];
    return TIME_OPTIONS.slice(0, -1).map(timeStr => {
      if (!currentRange) return { time: timeStr, disabled: false };
      const startIdx = timeToIndex(timeStr);
      if (startIdx > currentRange.end) {
        return { time: timeStr, disabled: true, reason: '끝시간 이후' };
      }
      if (wouldOverlap(startIdx, currentRange.end, currentRangeIndex)) {
        return { time: timeStr, disabled: true, reason: '다른 구간과 겹침' };
      }
      return { time: timeStr, disabled: false };
    });
  };

  // 끝시간 옵션: 모든 시간 표시 + 선택 불가 항목 disabled 마킹
  const getEndTimeOptions = (currentRangeIndex) => {
    const currentRange = ranges[currentRangeIndex];
    return TIME_OPTIONS.slice(1).map(timeStr => {
      if (!currentRange) return { time: timeStr, disabled: false };
      const endIdx = timeToIndex(timeStr) - 1; // 끝시간은 exclusive이므로 -1
      if (endIdx < currentRange.start) {
        return { time: timeStr, disabled: true, reason: '시작시간 이전' };
      }
      if (wouldOverlap(currentRange.start, endIdx, currentRangeIndex)) {
        return { time: timeStr, disabled: true, reason: '다른 구간과 겹침' };
      }
      return { time: timeStr, disabled: false };
    });
  };

  // 새로운 시간 범위 추가
  const addTimeRange = () => {
    // 기본 시간 범위 (09:00-18:00)
    let newStart = 18; // 09:00
    let newEnd = 35;   // 17:30 (18:00 - 1)
    
    // 겹치지 않는 시간 찾기
    let attempts = 0;
    while (attempts < 48 && wouldOverlap(newStart, newEnd)) {
      newStart += 2; // 1시간씩 뒤로 이동
      newEnd += 2;
      if (newEnd > 47) { // 24시간을 넘어가면 앞쪽에서 찾기
        newStart = 0;
        newEnd = 7; // 4시간 범위
      }
      attempts++;
    }
    
    if (attempts >= 48) {
      // 더 이상 추가할 수 없는 경우
      alert('더 이상 시간 범위를 추가할 수 없습니다.');
      return;
    }
    
    onChange([...ranges, { start: newStart, end: newEnd }]);
  };

  // 시간 범위 삭제
  const removeTimeRange = (index) => {
    const newRanges = ranges.filter((_, i) => i !== index);
    onChange(newRanges);
  };

  // 시간 범위 업데이트
  const updateTimeRange = (index, field, value) => {
    const newRanges = [...ranges];
    
    if (field === 'start') {
      const newStart = timeToIndex(value);
      newRanges[index].start = newStart;
      
      // 끝시간이 시작시간보다 작으면 조정 (같은 경우는 허용)
      if (newRanges[index].end < newStart) {
        newRanges[index].end = newStart; // 시작시간과 같게 (최소 30분 차이)
      }
    } else {
      const newEnd = timeToIndex(value) - 1; // 끝시간은 exclusive
      newRanges[index].end = newEnd;
      
      // 시작시간이 끝시간보다 크면 조정 (같은 경우는 허용)
      if (newRanges[index].start > newEnd) {
        newRanges[index].start = newEnd; // 끝시간과 같게 (최소 30분 차이)
      }
    }
    
    onChange(newRanges);
  };

  // 복사 기능
  const handleCopyClick = (e) => {
    setCopyAnchorEl(e.currentTarget);
    setCopyChecked([]);
  };

  const handleCopyClose = () => {
    setCopyAnchorEl(null);
  };

  const handleCopyCheck = (idx) => (e) => {
    if (e.target.checked) {
      setCopyChecked(prev => [...prev, idx]);
    } else {
      setCopyChecked(prev => prev.filter(i => i !== idx));
    }
  };

  const handleCopyApply = () => {
    if (!setAllRanges) return;
    const newAll = { ...allRanges };
    copyChecked.forEach(idx => {
      newAll[weekdayLabelList[idx]] = [...ranges];
    });
    setAllRanges(newAll);
    setCopyAnchorEl(null);
  };

  return (
    <Box sx={{ py: smUp ? 2 : 1 }}>
      {/* 요일 헤더 */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: smUp ? 2 : 1 }}>
        <Typography variant={smUp ? "subtitle1" : "body1"} sx={{ fontWeight: 600 }}>
          {weekdayName}
        </Typography>
        <Chip 
          label={`${ranges.length}`} 
          size="small" 
          variant="outlined"
          sx={{ 
            fontSize: '0.75rem',
            height: smUp ? 24 : 20,
            '& .MuiChip-label': { px: smUp ? 1 : 0.5 }
          }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <IconButton 
          size="small" 
          onClick={handleCopyClick}
          sx={{ color: 'text.secondary', p: smUp ? 1 : 0.5 }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* 시간 범위 리스트 */}
      <Stack spacing={smUp ? 1.5 : 1}>
        {ranges.map((range, index) => (
          <Paper 
            key={index} 
            variant="outlined" 
            sx={{ 
              p: smUp ? 1.5 : 1,
              borderRadius: 1,
              border: smUp ? undefined : 'none',
              bgcolor: smUp ? undefined : 'grey.50',
              '&:hover': { bgcolor: smUp ? 'grey.50' : 'grey.100' }
            }}
          >
            <Stack 
              direction="row" 
              alignItems="center" 
              spacing={smUp ? 2 : 1}
              sx={{ 
                flexWrap: smUp ? 'nowrap' : 'wrap',
                gap: smUp ? 2 : 0
              }}
            >
              {/* 시작 시간 */}
              <FormControl size="small" sx={{ minWidth: smUp ? 80 : 70 }}>
                <InputLabel sx={{ fontSize: smUp ? undefined : '0.875rem' }}>시작</InputLabel>
                <Select
                  value={indexToTime(range.start)}
                  onChange={(e) => updateTimeRange(index, 'start', e.target.value)}
                  label="시작"
                  sx={{ fontSize: smUp ? undefined : '0.875rem' }}
                  MenuProps={{ PaperProps: { sx: { minWidth: 180 } } }}
                >
                  {getStartTimeOptions(index).map(({ time, disabled, reason }) => (
                    <MenuItem
                      key={time}
                      value={time}
                      disabled={disabled}
                      sx={{ fontSize: smUp ? undefined : '0.875rem' }}
                    >
                      <Stack direction="row" alignItems="center" sx={{ width: '100%' }}>
                        <span>{time}</span>
                        {disabled && (
                          <Typography
                            variant="caption"
                            sx={{ ml: 'auto', pl: 1, color: 'text.disabled', fontSize: '0.7rem' }}
                          >
                            {reason}
                          </Typography>
                        )}
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant={smUp ? "body2" : "caption"} sx={{ color: 'text.secondary', px: smUp ? 0 : 0.5 }}>
                ~
              </Typography>

              {/* 끝 시간 */}
              <FormControl size="small" sx={{ minWidth: smUp ? 80 : 70 }}>
                <InputLabel sx={{ fontSize: smUp ? undefined : '0.875rem' }}>끝</InputLabel>
                <Select
                  value={indexToTime(range.end + 1)} // end는 inclusive이므로 +1
                  onChange={(e) => updateTimeRange(index, 'end', e.target.value)}
                  label="끝"
                  sx={{ fontSize: smUp ? undefined : '0.875rem' }}
                  MenuProps={{ PaperProps: { sx: { minWidth: 180 } } }}
                >
                  {getEndTimeOptions(index).map(({ time, disabled, reason }) => (
                    <MenuItem
                      key={time}
                      value={time}
                      disabled={disabled}
                      sx={{ fontSize: smUp ? undefined : '0.875rem' }}
                    >
                      <Stack direction="row" alignItems="center" sx={{ width: '100%' }}>
                        <span>{time}</span>
                        {disabled && (
                          <Typography
                            variant="caption"
                            sx={{ ml: 'auto', pl: 1, color: 'text.disabled', fontSize: '0.7rem' }}
                          >
                            {reason}
                          </Typography>
                        )}
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ flexGrow: 1 }} />

              {/* 삭제 버튼 */}
              <IconButton 
                size="small" 
                onClick={() => removeTimeRange(index)}
                sx={{ 
                  color: 'error.main',
                  p: smUp ? 1 : 0.5,
                  ml: smUp ? 0 : 'auto'
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Paper>
        ))}

        {/* 시간 범위 추가 버튼 */}
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addTimeRange}
          size={smUp ? "medium" : "small"}
          sx={{ 
            borderStyle: 'dashed', 
            py: 1,
            borderRadius: 1,
            fontSize: smUp ? undefined : '0.875rem',
            '&:hover': { 
              borderStyle: 'dashed',
              bgcolor: 'primary.50'
            }
          }}
        >
          시간 범위 추가
        </Button>
      </Stack>

      {/* 복사 팝오버 */}
      <Popover
        open={Boolean(copyAnchorEl)}
        anchorEl={copyAnchorEl}
        onClose={handleCopyClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: smUp ? 3 : 2, minWidth: smUp ? 240 : 200, maxWidth: smUp ? undefined : 280 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontSize: smUp ? undefined : '0.875rem' }}>
            다른 요일로 복사
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, fontSize: smUp ? undefined : '0.75rem' }}>
            {weekdayName}의 시간 설정을 복사할 요일을 선택하세요
          </Typography>
          <Divider sx={{ my: 2 }} />
          <FormGroup>
            {weekdayLabelList.map((label, idx) => (
              <FormControlLabel
                key={label}
                control={
                  <Checkbox 
                    checked={copyChecked.includes(idx)} 
                    onChange={handleCopyCheck(idx)} 
                    disabled={idx === weekdayIndex}
                    size="small"
                  />
                }
                label={label}
                sx={{ 
                  '& .MuiFormControlLabel-label': { 
                    fontSize: smUp ? '0.875rem' : '0.75rem' 
                  }
                }}
              />
            ))}
          </FormGroup>
          <Stack direction="row" spacing={1} sx={{ mt: 3, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={handleCopyClose} sx={{ fontSize: smUp ? undefined : '0.75rem' }}>
              취소
            </Button>
            <Button 
              size="small" 
              variant="contained" 
              onClick={handleCopyApply} 
              disabled={copyChecked.length === 0}
              sx={{ fontSize: smUp ? undefined : '0.75rem' }}
            >
              복사
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
};

export default WeekdayTimeRangeSelector; 